export type IRLStatus = "open" | "fulfilled" | "partial" | "withdrawn";
export type IRLPriority = "high" | "medium" | "low";

export interface IRLRequest {
  id: string;
  number: string;
  topic?: string;
  entityName?: string;
  /** Advising firm assigned to this request (e.g., "Leo Berwick"). Used for filtering. */
  advisor?: string;
  request: string;
  status: IRLStatus;
  /**
   * Triage priority. Optional for backwards compat — pre-priority rows render
   * as Medium in the UI but stay `undefined` in storage until the user edits
   * them. New rows (manual / generated / notes-extracted) always carry one.
   */
  priority?: IRLPriority;
  /**
   * 0-based row index in the SOURCE matrix this request was parsed from at
   * upload time. Lets Export update the corresponding original cell in-place
   * (preserving formatting + unmapped cells). Undefined for any row added
   * after upload (manual `+ Add row`, notes-extracted) — those become
   * fresh rows appended below the original data area on Export.
   */
  originalRowIndex?: number;
  /**
   * Fields the user has actually edited since upload. Export only writes
   * cells for fields in this set — untouched cells stay byte-identical to
   * the source file (no normalized-label overwrites). New rows (no
   * originalRowIndex) ignore this and write every mapped field.
   */
  editedFields?: IRLFieldKey[];
  /**
   * Raw cell text captured at upload time, keyed by field. When a field is
   * NOT in editedFields, Export writes this back (or leaves the cell alone
   * if missing) instead of writing a normalized label like "Fulfilled".
   */
  rawValues?: Partial<Record<IRLFieldKey, string>>;
}

export type IRLFieldKey = "number" | "topic" | "entityName" | "advisor" | "request" | "status" | "priority";

export interface IRLFieldDef {
  key: IRLFieldKey;
  label: string;
  required: boolean;
  hints: string[]; // normalized header keywords used by auto-mapping
}

export const IRL_FIELDS: IRLFieldDef[] = [
  { key: "number", label: "IRL #", required: false, hints: ["irl", "irl no", "irl number", "request no", "request number", "no", "number", "id", "ref", "#"] },
  { key: "topic", label: "Topic", required: false, hints: ["topic", "category", "section", "area", "type", "workstream", "subject"] },
  { key: "entityName", label: "Entity Name", required: false, hints: ["entity", "entity name", "company", "subsidiary", "legal entity"] },
  { key: "advisor", label: "Advisor", required: false, hints: ["advisor", "adviser", "firm", "party", "owner", "responsible", "assigned to", "team", "workstream owner", "lb", "leo berwick"] },
  { key: "request", label: "Request", required: true, hints: ["request", "description", "ask", "details", "information requested", "requested information", "item"] },
  { key: "status", label: "Status", required: false, hints: ["status", "state", "progress"] },
  { key: "priority", label: "Priority", required: false, hints: ["priority", "importance", "urgency", "rank", "tier"] },
];

export interface IRLMeta {
  uploadedAt: string;
  originalFileName: string;
  count: number;
  /**
   * Header row from the original spreadsheet — preserved so Export can
   * round-trip the IRL into the same column layout it was uploaded with.
   * Optional because generated IRLs and pre-existing saves don't carry it.
   */
  originalHeaders?: string[];
  /**
   * Column-by-column mapping captured during upload — entry `i` is the
   * IRL field the user assigned to column `i` of the original file
   * (or `""` if that column was unmapped / "Ignore"). Same length as
   * `originalHeaders`.
   */
  originalMapping?: (IRLFieldKey | "")[];
  /**
   * The original .xlsx/.xls/.csv file bytes (base64-encoded), captured at
   * upload time. Lets Export load the file with ExcelJS and edit it in
   * place — preserving fonts, fills, borders, widths, title rows, and
   * all unmapped cell content. Optional / may be missing for old saves
   * that pre-date this capability OR if the file exceeded localStorage
   * quota; in those cases Export falls back to a clean built-from-scratch
   * layout.
   */
  originalFileBytes?: string;
  /** 0-based index of the header row in the source matrix. */
  originalHeaderRowIdx?: number;
  /**
   * 0-based index of the LAST data row that produced an IRLRequest at
   * upload time. Used as the insert point for new rows on Export — anything
   * below it (totals, footers, signatures) gets pushed down, not overwritten.
   */
  originalLastDataRowIdx?: number;
  /**
   * The exact set of 0-based row indices that became IRLRequests at upload.
   * Lets Export distinguish "user deleted an imported row" from "row was
   * never imported (e.g., blank separator row)". Without this, blank gaps
   * in the source look like deletions and force the ExcelJS round-trip
   * even on no-change exports — which then mangles sheets we never touch.
   */
  originalImportedRowIndices?: number[];
}
